const SUITS = ["s", "h", "d", "c"];

export const RANK_LABEL = {
  1: "A",
  11: "J",
  12: "Q",
  13: "K",
};

export const SUIT_SYMBOL = {
  s: "♠",
  h: "♥",
  d: "♦",
  c: "♣",
};

export const SUIT_NAME = {
  s: "spades",
  h: "hearts",
  d: "diamonds",
  c: "clubs",
};

export const isRed = (suit) => suit === "h" || suit === "d";

export const rankLabel = (rank) => RANK_LABEL[rank] || String(rank);

export function makeDeck() {
  const cards = [];
  SUITS.forEach((suit) => {
    for (let rank = 1; rank <= 13; rank += 1) {
      cards.push({ id: `${suit}${rank}`, suit, rank, faceUp: false });
    }
  });
  return cards;
}

export function shuffle(cards, random = Math.random) {
  const next = cards.map((card) => ({ ...card }));
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

const copyCard = (card) => ({
  id: card.id,
  suit: card.suit,
  rank: card.rank,
  faceUp: card.faceUp,
});

export function copyState(state) {
  return {
    tableau: state.tableau.map((pile) => pile.map(copyCard)),
    foundations: state.foundations.map((pile) => pile.map(copyCard)),
    stock: state.stock.map(copyCard),
    waste: state.waste.map(copyCard),
  };
}

export function createGame(random = Math.random) {
  const deck = shuffle(makeDeck(), random);
  const tableau = [];
  let index = 0;
  for (let col = 0; col < 7; col += 1) {
    const pile = [];
    for (let row = 0; row <= col; row += 1) {
      const card = deck[index];
      index += 1;
      pile.push({ ...card, faceUp: row === col });
    }
    tableau.push(pile);
  }
  return {
    tableau,
    foundations: [[], [], [], []],
    stock: deck.slice(index).map((card) => ({ ...card, faceUp: false })),
    waste: [],
  };
}

const opposite = (a, b) => isRed(a) !== isRed(b);

export function isRun(cards) {
  if (!cards.length || cards.some((card) => !card.faceUp)) return false;
  for (let i = 1; i < cards.length; i += 1) {
    const prev = cards[i - 1];
    const card = cards[i];
    if (card.rank !== prev.rank - 1 || !opposite(prev.suit, card.suit)) return false;
  }
  return true;
}

export function canPlaceOnTableau(card, top) {
  if (!top) return card.rank === 13;
  return opposite(card.suit, top.suit) && card.rank === top.rank - 1;
}

export function canPlaceOnFoundation(card, pile) {
  if (!pile.length) return card.rank === 1;
  const top = pile[pile.length - 1];
  return card.suit === top.suit && card.rank === top.rank + 1;
}

function extract(state, from) {
  if (from.zone === "waste") {
    if (!state.waste.length) return null;
    return [state.waste.pop()];
  }
  if (from.zone === "foundation") {
    const pile = state.foundations[from.pile];
    if (!pile || !pile.length) return null;
    return [pile.pop()];
  }
  if (from.zone === "tableau") {
    const pile = state.tableau[from.col];
    if (!pile || from.index < 0 || from.index >= pile.length) return null;
    const cards = pile.slice(from.index);
    if (!isRun(cards)) return null;
    pile.splice(from.index);
    const top = pile[pile.length - 1];
    if (top && !top.faceUp) top.faceUp = true;
    return cards;
  }
  return null;
}

function place(state, cards, to) {
  if (to.zone === "tableau") {
    const pile = state.tableau[to.col];
    if (!pile) return false;
    const top = pile[pile.length - 1] || null;
    if (!canPlaceOnTableau(cards[0], top)) return false;
    pile.push(...cards);
    return true;
  }
  if (to.zone === "foundation") {
    if (cards.length !== 1) return false;
    const indexes =
      to.pile == null ? [0, 1, 2, 3] : [to.pile];
    for (const index of indexes) {
      const pile = state.foundations[index];
      if (pile && canPlaceOnFoundation(cards[0], pile)) {
        pile.push(cards[0]);
        return true;
      }
    }
  }
  return false;
}

export function moveCards(state, from, to) {
  const next = copyState(state);
  const cards = extract(next, from);
  if (!cards || !place(next, cards, to)) return null;
  return next;
}

export function drawCard(state) {
  const next = copyState(state);
  if (next.stock.length) {
    const card = next.stock.pop();
    card.faceUp = true;
    next.waste.push(card);
    return next;
  }
  if (next.waste.length) {
    next.stock = [...next.waste].reverse().map((card) => ({ ...card, faceUp: false }));
    next.waste = [];
    return next;
  }
  return null;
}

export function isWon(state) {
  return state.foundations.every((pile) => pile.length === 13);
}

const hintKey = (hint) =>
  hint.draw
    ? "draw"
    : `${hint.from.zone}:${hint.from.col ?? ""}:${hint.from.index ?? ""}:${hint.from.pile ?? ""}>${hint.to.zone}:${hint.to.col ?? ""}:${hint.to.pile ?? ""}`;

export function listHints(state) {
  const seen = new Set();
  const hints = [];
  const add = (from, to, score) => {
    if (!moveCards(state, from, to)) return;
    const hint = { from, to, score };
    const key = hintKey(hint);
    if (seen.has(key)) return;
    seen.add(key);
    hints.push(hint);
  };

  const stopAfterFirst = (visit) => {
    const before = hints.length;
    visit();
    return hints.length > before;
  };

  if (state.waste.length) {
    for (let pile = 0; pile < 4; pile += 1) {
      if (stopAfterFirst(() => add({ zone: "waste" }, { zone: "foundation", pile }, 100))) {
        break;
      }
    }
  }

  state.tableau.forEach((pile, col) => {
    if (!pile.length) return;
    const topIndex = pile.length - 1;
    if (!pile[topIndex].faceUp) return;
    for (let pileIndex = 0; pileIndex < 4; pileIndex += 1) {
      if (
        stopAfterFirst(() =>
          add(
            { zone: "tableau", col, index: topIndex },
            { zone: "foundation", pile: pileIndex },
            90
          )
        )
      ) {
        break;
      }
    }
  });

  state.tableau.forEach((pile, col) => {
    let usedEmpty = false;
    for (let index = 0; index < pile.length; index += 1) {
      if (!pile[index].faceUp || !isRun(pile.slice(index))) continue;
      const uncovers = index > 0 && !pile[index - 1].faceUp;
      for (let dest = 0; dest < 7; dest += 1) {
        if (dest === col) continue;
        const destEmpty = state.tableau[dest].length === 0;
        if (destEmpty && usedEmpty) continue;
        const before = hints.length;
        add(
          { zone: "tableau", col, index },
          { zone: "tableau", col: dest },
          uncovers ? 70 : 30
        );
        if (destEmpty && hints.length > before) usedEmpty = true;
      }
    }
  });

  if (state.waste.length) {
    let usedEmpty = false;
    for (let col = 0; col < 7; col += 1) {
      const destEmpty = state.tableau[col].length === 0;
      if (destEmpty && usedEmpty) continue;
      const before = hints.length;
      add({ zone: "waste" }, { zone: "tableau", col }, 50);
      if (destEmpty && hints.length > before) usedEmpty = true;
    }
  }

  if (drawCard(state)) hints.push({ draw: true, score: 10 });

  hints.sort((a, b) => b.score - a.score);
  return hints;
}

export function sameSelection(a, b) {
  if (!a || !b || a.zone !== b.zone) return false;
  if (a.zone === "waste") return true;
  if (a.zone === "foundation") return a.pile === b.pile;
  return a.col === b.col && a.index === b.index;
}
